/**
 * FASE 22.4 — FOLLOWUP ENGINE
 *
 * Detects overdue, stagnant, and unresolved workflows.
 * Works entirely on in-memory workflow state — no new Supabase calls.
 */

import { getActiveWorkflows } from "./workflowState";
import { getWorkflowDefinition, WORKFLOW_STATUS, STEP_STATUS } from "./workflowSchemas";
import { scoreWorkflow } from "./workflowPriorities";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysSince(ms) {
  return (Date.now() - ms) / MS_PER_DAY;
}

/**
 * @typedef {Object} FollowupItem
 * @property {"overdue"|"stagnant"|"no_progress"|"near_limit"} kind
 * @property {string} workflowId
 * @property {string} workflowType
 * @property {string|null} entityName
 * @property {string} label — pastoral display string
 * @property {number} daysSinceActivity
 * @property {number} score
 */

/**
 * Compute all followup items across active workflows.
 * @param {Workflow[]} [workflows] — override; defaults to getActiveWorkflows()
 * @returns {FollowupItem[]}
 */
export function computeFollowupItems(workflows) {
  const wfs = workflows ?? getActiveWorkflows();
  const items = [];

  for (const wf of wfs) {
    const def         = getWorkflowDefinition(wf.type);
    const inactDays   = daysSince(wf.last_activity);
    const ageDays     = daysSince(wf.created_at);
    const entityName  = wf.related_entities?.[0]?.name ?? null;
    const { score }   = scoreWorkflow(wf);

    // Overdue: past maxInactiveDays threshold
    if (def?.maxInactiveDays && inactDays >= def.maxInactiveDays) {
      items.push({
        kind:               "overdue",
        workflowId:         wf.id,
        workflowType:       wf.type,
        entityName,
        label:              entityName
          ? `${def.label} para ${entityName} lleva ${Math.round(inactDays)} días sin actividad`
          : `${def.label} lleva ${Math.round(inactDays)} días sin actividad`,
        daysSinceActivity:  Math.round(inactDays),
        score,
      });
      continue;
    }

    // Near limit: ≥70% of maxInactiveDays
    if (def?.maxInactiveDays && inactDays >= def.maxInactiveDays * 0.7) {
      items.push({
        kind:               "near_limit",
        workflowId:         wf.id,
        workflowType:       wf.type,
        entityName,
        label:              entityName
          ? `${def.label} para ${entityName} pronto necesitará atención`
          : `${def.label} pronto necesitará atención`,
        daysSinceActivity:  Math.round(inactDays),
        score,
      });
      continue;
    }

    // No progress after >7 days
    const totalSteps   = wf.steps?.length ?? 0;
    const doneSteps    = wf.steps?.filter(
      (s) => s.status === STEP_STATUS.DONE || s.status === STEP_STATUS.SKIPPED,
    ).length ?? 0;

    if (ageDays > 7 && doneSteps === 0 && totalSteps > 0) {
      items.push({
        kind:               "no_progress",
        workflowId:         wf.id,
        workflowType:       wf.type,
        entityName,
        label:              entityName
          ? `${def?.label ?? wf.type} para ${entityName} no tiene pasos completados`
          : `${def?.label ?? wf.type} no tiene pasos completados`,
        daysSinceActivity:  Math.round(inactDays),
        score,
      });
      continue;
    }

    // Stagnant: ACTIVE but low progress after 14+ days
    if (ageDays > 14 && wf.progress < 0.4) {
      items.push({
        kind:               "stagnant",
        workflowId:         wf.id,
        workflowType:       wf.type,
        entityName,
        label:              entityName
          ? `${def?.label ?? wf.type} para ${entityName} tiene progreso bajo`
          : `${def?.label ?? wf.type} tiene progreso bajo`,
        daysSinceActivity:  Math.round(inactDays),
        score,
      });
    }
  }

  // Sort by score descending
  return items.sort((a, b) => b.score - a.score);
}

/** Returns workflows where inactivity exceeds maxInactiveDays. */
export function getOverdueWorkflows() {
  return getActiveWorkflows().filter((wf) => {
    const def = getWorkflowDefinition(wf.type);
    if (!def?.maxInactiveDays) return false;
    return daysSince(wf.last_activity) >= def.maxInactiveDays;
  });
}

/** Returns workflows that are ACTIVE but haven't advanced in `thresholdDays`. */
export function getStagnantWorkflows(thresholdDays = 7) {
  return getActiveWorkflows().filter((wf) => {
    return (
      wf.status === WORKFLOW_STATUS.ACTIVE &&
      daysSince(wf.last_activity) >= thresholdDays &&
      wf.progress < 0.5
    );
  });
}
